import { createMoneyInputPresentation } from './money-input-presentation';

// Localized card-limit editor. Amounts stay strings all the way to the hidden
// canonical field. Only the locale decimal separator can be typed; grouping is
// presentation. Pasted/replaced text is validated as a whole before accepting it.
// A small explicit edit history preserves both value and caret across grouping,
// including native/mobile history events (rewriting .value loses native history).

const instances = new WeakMap();
const MAX_MINOR_UNITS = '1000000000000';
// Ruby's String#strip: ASCII whitespace and NUL, never NBSP or other spaces.
const RUBY_STRIP = /^[\t\n\v\f\r \0]+|[\t\n\v\f\r \0]+$/g;

// String arithmetic only: no binary-float conversion of a money amount.
function canonicalAmount(value, precision) {
  const match = /^(\d{1,15})(?:\.(\d{1,4}))?$/.exec(value);
  if (!match || (match[2] || '').length > precision) return null;
  const whole = match[1].replace(/^0+(?=\d)/, '');
  const fraction = (match[2] || '').replace(/0+$/, '');
  const minor = (whole + fraction.padEnd(precision, '0')).replace(/^0+/, '') || '0';
  if (minor.length > MAX_MINOR_UNITS.length ||
      (minor.length === MAX_MINOR_UNITS.length && minor > MAX_MINOR_UNITS)) return null;
  return whole + (fraction ? `.${fraction}` : '');
}

function escapePattern(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function initialize(container) {
  if (instances.has(container)) return;
  const display = container.querySelector('[data-card-limit-display]');
  const hidden = container.querySelector('[data-card-limit-value]');
  if (!display || !hidden || !hidden.name) return;
  const precision = Number.parseInt(container.dataset.precision, 10);
  const currency = container.dataset.currency || '';
  const locale = (container.dataset.locale || '').toLowerCase();
  // Without them the field stays as the server rendered it: the visible text is
  // submitted and the server reads it with its locale marker.
  if (!Number.isInteger(precision) || precision < 0 || precision > 4 || !currency || !locale) return;
  const english = locale.startsWith('en');
  const decimal = english ? '.' : ',';
  const group = english ? ',' : '.';
  // The view passes the translated messages; Spanish is only the fallback.
  const errorMessage = container.dataset.invalidMessage || (precision === 0
    ? 'Escribe un monto entero positivo. Usa punto para miles.'
    : 'Escribe un monto positivo. Usa coma para decimales y punto para miles.');
  const ambiguousMessage = container.dataset.ambiguousMessage || errorMessage;
  // The server grammar: digits alone (up to 15) or groups of three after 1-3
  // digits, then optionally the decimal separator and at least one digit.
  const grammar = new RegExp(`^(\\d{1,15}|[1-9]\\d{0,2}(?:${escapePattern(group)}\\d{3})+)` +
    `(?:${escapePattern(decimal)}(\\d+))?$`);

  // More decimals than the currency has (50,000 in es, 5,000 CLP) is thousands
  // written with the other convention: ambiguous, never read as 50 or 5. Zero
  // and amounts above the maximum are invalid, as on the server.
  function classify(text) {
    const value = text.replace(RUBY_STRIP, '');
    if (value === '') return { state: 'empty', canonical: '' };
    const match = grammar.exec(value);
    if (!match) return { state: 'invalid' };
    const fraction = match[2] || '';
    if (fraction.length > Math.min(precision, 2)) return { state: 'ambiguous' };
    const whole = match[1].split(group).join('');
    const canonical = canonicalAmount(fraction ? `${whole}.${fraction}` : whole, precision);
    if (canonical === null || canonical === '0') return { state: 'invalid' };
    return { state: 'valid', canonical };
  }

  // Never a canonical amount while invalid: the currency code makes even a
  // submit that skipped browser validation fail on the server, not misread.
  function submittedValue(result, text) {
    if (result.state === 'valid' || result.state === 'empty') return result.canonical;
    return `${text.replace(RUBY_STRIP, '')} ${currency}`;
  }

  let presentation = { refresh() {}, destroy() {} };

  // Reads `text` (what the field shows, or is about to show) into the hidden
  // value and the field's validity.
  function update(text = display.value) {
    const result = classify(text);
    hidden.value = submittedValue(result, text);
    const message = { ambiguous: ambiguousMessage, invalid: errorMessage }[result.state] || '';
    display.setCustomValidity(message);
    display.setAttribute('aria-invalid', message ? 'true' : 'false');
    presentation.refresh();
    return result;
  }

  const maxDecimals = Math.min(precision, 2);
  const editable = new RegExp(`^\\d*(?:${escapePattern(decimal)}\\d{0,${maxDecimals}})?$`);
  const snapshot = () => ({ value: display.value, start: display.selectionStart,
    end: display.selectionEnd, direction: display.selectionDirection });
  let current = snapshot();
  let before;
  let composition;
  let composing = false;
  let history = [current];
  let historyIndex = 0;

  function restore(state) {
    display.value = state.value;
    display.setSelectionRange(state.start, state.end, state.direction);
    current = snapshot();
    update();
  }

  function remember(previous) {
    if (current.value === previous.value) return;
    history[historyIndex] = previous;
    history.splice(historyIndex + 1);
    history.push(current);
    // Bound memory without changing the place value or caret of retained edits.
    if (history.length > 100) history.shift();
    historyIndex = history.length - 1;
  }

  function replay(direction) {
    if (display.readOnly || display.disabled) return;
    const next = Math.max(0, Math.min(history.length - 1, historyIndex + direction));
    if (next === historyIndex) return;
    history[historyIndex] = snapshot();
    historyIndex = next;
    restore(history[historyIndex]);
    before = undefined;
  }

  function rawIndex(text, index) {
    return text.slice(0, index).split(group).join('').length;
  }

  function displayIndex(text, index) {
    let count = 0;
    for (let cursor = 0; cursor < text.length; cursor += 1) {
      if (count === index) return cursor;
      if (text[cursor] !== group) count += 1;
    }
    return text.length;
  }

  // Returns the formatted edit and its caret, or rejects the entire edit. No
  // filtering of e.g. "1e3", no rounding a third decimal, no float conversion.
  function formatted(raw, caret) {
    if (!editable.test(raw) || (maxDecimals === 0 && raw.includes(decimal))) return null;
    if (raw === '') return { value: '', start: 0, end: 0 };
    if (raw.startsWith(decimal)) {
      raw = `0${raw}`;
      caret += 1;
    }
    const [whole, fraction] = raw.split(decimal);
    const normalized = whole.replace(/^0+(?=\d)/, '');
    const removed = whole.length - normalized.length;
    caret = Math.max(0, caret - Math.min(caret, removed));
    const amount = normalized + (fraction ? `.${fraction}` : '');
    if (canonicalAmount(amount, precision) === null) return null;
    const grouped = normalized.replace(/\B(?=(\d{3})+(?!\d))/g, group);
    const value = grouped + (fraction !== undefined ? `${decimal}${fraction}` : '');
    const position = displayIndex(value, caret);
    return { value, start: position, end: position };
  }

  function acceptInput(event = {}) {
    if (composing || event.isComposing) return;
    if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
      restore(current);
      replay(event.inputType === 'historyUndo' ? -1 : 1);
      return;
    }
    const previous = before || current;
    before = undefined;
    if (display.readOnly || display.disabled) return restore(previous);
    const value = display.value;
    if (value === previous.value) { current = snapshot(); update(); return; }

    // Browser editing normally changes precisely the selected range. Deriving
    // the changed range also covers autofill, drop and input without keydown or
    // beforeinput. The prefix/suffix are read from the last accepted visible text.
    let start = 0;
    while (start < previous.value.length && start < value.length && previous.value[start] === value[start]) start += 1;
    let oldEnd = previous.value.length;
    let newEnd = value.length;
    while (oldEnd > start && newEnd > start && previous.value[oldEnd - 1] === value[newEnd - 1]) {
      oldEnd -= 1;
      newEnd -= 1;
    }
    // Repeated digits make a textual diff ambiguous. Prefer the browser's
    // selection whenever it explains the edit, so the caret keeps its position.
    if (previous.start !== null && value.startsWith(previous.value.slice(0, previous.start)) &&
        value.endsWith(previous.value.slice(previous.end)) &&
        value.length >= previous.start + previous.value.length - previous.end &&
        !event.inputType?.startsWith('delete')) {
      start = previous.start;
      oldEnd = previous.end;
      newEnd = value.length - (previous.value.length - previous.end);
    }
    if (event.inputType?.startsWith('delete') && previous.value.length > value.length) {
      const deletionStart = Math.min(previous.start, display.selectionStart);
      const deletionEnd = deletionStart + previous.value.length - value.length;
      if (previous.value.slice(0, deletionStart) + previous.value.slice(deletionEnd) === value) {
        start = deletionStart;
        oldEnd = deletionEnd;
        newEnd = deletionStart;
      }
    }
    let inserted = value.slice(start, newEnd);
    const replacingAll = start === 0 && oldEnd === previous.value.length;
    // A literal rejected by the server can be shown again. Never treat its
    // malformed punctuation as automatic grouping during a partial correction.
    if (previous.value.includes(group) && !grammar.test(previous.value) && !replacingAll) {
      const correction = grammar.test(value) ? formatted(value.split(group).join(''), rawIndex(value, display.selectionStart)) : null;
      if (!correction) return restore(previous);
      restore(correction);
      remember(previous);
      return;
    }
    if (inserted.includes(group)) {
      // Grouped clipboard/autofill text is unambiguous only as a complete amount.
      // A lone typed group separator is never accepted as a decimal shortcut.
      if (!replacingAll || inserted.length === 1 || !grammar.test(inserted)) return restore(previous);
      inserted = inserted.split(group).join('');
    }
    if (!editable.test(inserted)) return restore(previous);
    let from = rawIndex(previous.value, start);
    let to = rawIndex(previous.value, oldEnd);
    const oldRaw = previous.value.split(group).join('');
    if (inserted === '' && from === to && start !== oldEnd && previous.start === previous.end) {
      // Backspace/delete beside automatic grouping removes the adjacent digit,
      // instead of endlessly deleting and repainting the same separator.
      if (event.inputType === 'deleteContentBackward') from = Math.max(0, from - 1);
      if (event.inputType === 'deleteContentForward') to = Math.min(oldRaw.length, to + 1);
    }
    const result = formatted(oldRaw.slice(0, from) + inserted + oldRaw.slice(to), from + inserted.length);
    if (!result) return restore(previous);
    restore(result);
    remember(previous);
  }

  const listeners = new AbortController();
  const { signal } = listeners;
  document.addEventListener('selectionchange', () => {
    if (!composing && document.activeElement === display && display.value === current.value) current = snapshot();
  }, { signal });
  display.addEventListener('keydown', event => {
    if (event.isComposing || event.keyCode === 229 || display.readOnly || display.disabled) return;
    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
      const key = event.key.toLowerCase();
      const redoY = key === 'y' && !/Mac|iPhone|iPad/.test(navigator.platform);
      if (key === 'z' || redoY) {
        event.preventDefault();
        replay(event.shiftKey || redoY ? 1 : -1);
      }
      return;
    }
    if (event.key.length === 1 && !/[0-9]/.test(event.key) && (event.key !== decimal || maxDecimals === 0)) {
      event.preventDefault();
    }
  }, { signal });
  display.addEventListener('beforeinput', event => {
    if (composing || event.isComposing) return;
    before = snapshot();
    if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
      if (event.cancelable) {
        event.preventDefault();
        replay(event.inputType === 'historyUndo' ? -1 : 1);
      }
    } else if (event.inputType === 'insertText' && event.data === group && event.cancelable) {
      event.preventDefault();
      before = undefined;
    }
  }, { signal });
  display.addEventListener('input', acceptInput, { signal });
  display.addEventListener('compositionstart', () => {
    composition = snapshot();
    composing = true;
    hidden.value = `${display.value} ${currency}`;
    display.setCustomValidity(errorMessage);
    display.setAttribute('aria-invalid', 'true');
  }, { signal });
  display.addEventListener('compositionend', () => {
    composing = false;
    before = composition;
    acceptInput({ inputType: 'insertCompositionText' });
    composition = undefined;
  }, { signal });
  const { form } = display;
  // Right after this event the browser restores the text the server rendered
  // (defaultValue); a hidden input keeps what it holds, so it is read now. The
  // timer re-reads what is shown in case another listener canceled the reset.
  form?.addEventListener('reset', () => {
    update(display.defaultValue);
    setTimeout(() => {
      if (display.isConnected) {
        current = snapshot();
        before = undefined;
        history = [current];
        historyIndex = 0;
        update();
      }
    });
  }, { signal });
  // Whatever happened before, the value submitted is the reading of the text
  // shown at submission time.
  form?.addEventListener('formdata', event => {
    if (!display.isConnected || hidden.disabled || hidden.form !== event.target) return;
    event.formData.set(hidden.name, composing ? `${display.value} ${currency}` : submittedValue(classify(display.value), display.value));
  }, { signal });

  try {
    presentation = createMoneyInputPresentation(display, () => ({ decimal, group, precision }), { namespace: 'card-limit-amount', dataPrefix: 'cardLimit' });
  } catch (_error) {
    presentation = { refresh() {}, destroy() {} };
  }
  update();
  // Only now does the hidden canonical field replace the visible one in the form.
  display.removeAttribute('name');
  hidden.disabled = false;
  container.dataset.cardLimitReady = 'true';
  instances.set(container, {
    destroy() {
      listeners.abort();
      presentation.destroy();
    },
  });
}

export function initializeCardLimitAmounts(root = document) {
  if (root.matches?.('.card-limit-amount')) initialize(root);
  root.querySelectorAll?.('.card-limit-amount').forEach(initialize);
}

function boot() {
  if (!document.querySelector('.card-limit-amount, [data-card-preferences-view]')) return;
  // Vue's application mount replaces server-rendered nodes. Observe only DOM
  // additions/removals; input value edits do not trigger this observer.
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.removedNodes.forEach(node => {
        if (node.isConnected) return;
        const removed = node.matches?.('.card-limit-amount') ? [node] : [];
        removed.push(...(node.querySelectorAll?.('.card-limit-amount') || []));
        removed.forEach(container => {
          instances.get(container)?.destroy();
          instances.delete(container);
        });
      });
      mutation.addedNodes.forEach(node => initializeCardLimitAmounts(node));
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  queueMicrotask(() => initializeCardLimitAmounts());
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
